import React, { useState, useRef, useEffect, useCallback, forwardRef } from 'react';
import styled from 'styled-components';

// --- Styled Components (unchanged) ---
const EditorContainer = styled.div`
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 20px 35px -12px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: box-shadow 0.2s ease;
  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;

  &:focus-within {
    box-shadow: 0 20px 35px -12px rgba(0, 0, 0, 0.15), 0 0 0 2px rgba(99, 102, 241, 0.3);
  }
`;

const ToolbarWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid #eef2f6;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 6px;
  border-right: 1px solid #e2e8f0;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 640px) {
    border-right: none;
    padding: 4px 0;
  }
`;

const IconButton = styled.button`
  background: ${({ $active }) => ($active ? '#eef2ff' : 'transparent')};
  border: none;
  border-radius: 10px;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  color: #1e293b;
  transition: all 0.2s cubic-bezier(0.2, 0.9, 0.4, 1.1);
  font-family: inherit;

  &:hover {
    background: #f1f5f9;
    transform: scale(0.96);
  }

  &:active {
    transform: scale(0.94);
    background: #e2e8f0;
  }

  svg {
    width: 18px;
    height: 18px;
    stroke-width: 1.8;
    stroke: currentColor;
    fill: none;
  }
`;

const StyledSelect = styled.select`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 6px 28px 6px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2347566b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;

  &:hover {
    background-color: #f1f5f9;
    border-color: #cbd5e1;
  }

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }
`;

const ColorPicker = styled.input`
  width: 36px;
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  cursor: pointer;
  padding: 4px;
  transition: all 0.2s;

  &:hover {
    border-color: #cbd5e1;
    transform: scale(0.96);
  }

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  &::-webkit-color-swatch {
    border: none;
    border-radius: 8px;
  }
`;

const EditorArea = styled.div`
  min-height: 380px;
  padding: 24px 28px;
  font-size: 16px;
  line-height: 1.6;
  outline: none;
  cursor: text;
  background: #ffffff;
  color: #0f172a;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  transition: background 0.1s ease;

  &:focus {
    background: #fefefe;
  }

  &:empty:before {
    content: attr(data-placeholder);
    color: #94a3b8;
    font-weight: 400;
    pointer-events: none;
  }

  h1 {
    font-size: 2rem;
    font-weight: 700;
    margin: 1rem 0 0.5rem;
  }
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0.75rem 0 0.5rem;
  }
  h3 {
    font-size: 1.25rem;
    font-weight: 600;
  }
  ul, ol {
    padding-left: 1.8rem;
  }
  blockquote {
    border-left: 4px solid #6366f1;
    margin: 0.5rem 0;
    padding-left: 1rem;
    color: #334155;
    font-style: italic;
  }
  a {
    color: #6366f1;
    text-decoration: underline;
    cursor: pointer;
  }
`;

// --- Icons (unchanged) ---
const Icons = {
  Bold: () => (
    <svg viewBox="0 0 24 24">
      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
    </svg>
  ),
  Italic: () => (
    <svg viewBox="0 0 24 24">
      <path d="M10 4h10M14 20H4M14 4l-4 16" />
    </svg>
  ),
  Underline: () => (
    <svg viewBox="0 0 24 24">
      <path d="M6 4v6a6 6 0 0 0 12 0V4" />
      <path d="M4 20h16" />
    </svg>
  ),
  AlignLeft: () => (
    <svg viewBox="0 0 24 24">
      <path d="M3 6h18M3 12h12M3 18h18" />
    </svg>
  ),
  AlignCenter: () => (
    <svg viewBox="0 0 24 24">
      <path d="M6 6h12M4 12h16M6 18h12" />
    </svg>
  ),
  AlignRight: () => (
    <svg viewBox="0 0 24 24">
      <path d="M3 6h18M9 12h12M3 18h18" />
    </svg>
  ),
  ListBullet: () => (
    <svg viewBox="0 0 24 24">
      <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
    </svg>
  ),
  ListNumber: () => (
    <svg viewBox="0 0 24 24">
      <path d="M10 6h11M10 12h11M10 18h11M4 6h1v4M4 10h2M6 18H4c0-1 1-2 2-2s2 1 2 2-1 2-2 2" />
    </svg>
  ),
  Link: () => (
    <svg viewBox="0 0 24 24">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  ),
  Unlink: () => (
    <svg viewBox="0 0 24 24">
      <path d="M17 7l-4 4M7 17l4-4M7 7l10 10" />
    </svg>
  ),
  Undo: () => (
    <svg viewBox="0 0 24 24">
      <path d="M3 7v6h6M3 13l4-4 4 4" />
      <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
    </svg>
  ),
  Redo: () => (
    <svg viewBox="0 0 24 24">
      <path d="M21 7v6h-6M21 13l-4-4-4 4" />
      <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
    </svg>
  ),
  Clear: () => (
    <svg viewBox="0 0 24 24">
      <path d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Quote: () => (
    <svg viewBox="0 0 24 24">
      <path d="M3 10H1v4h2v-4zm18 0h-2v4h2v-4zM5 6h2v12H5zm12 0h2v12h-2z" />
    </svg>
  ),
};

// --- Enhanced Editor Component with forwardRef and onChange ---
const ProEditor = forwardRef(({ initialValue = '', onChange, placeholder = '✨ Start writing your content here... (supports rich formatting)' }, forwardedRef) => {
  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);

  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    justifyLeft: false,
    justifyCenter: false,
    justifyRight: false,
    insertUnorderedList: false,
    insertOrderedList: false,
  });

  // Combine forwarded ref with internal ref
  const setRefs = useCallback(
    (node) => {
      editorRef.current = node;
      if (forwardedRef) {
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else {
          forwardedRef.current = node;
        }
      }
    },
    [forwardedRef]
  );

  // Initialize content with initialValue
  useEffect(() => {
    if (editorRef.current && initialValue !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = initialValue;
    }
  }, [initialValue]);

  // Emit content changes
  const handleInput = useCallback(() => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const saveSelection = useCallback(() => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editorRef.current?.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
      }
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (savedRangeRef.current) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(savedRangeRef.current);
    }
  }, []);

  const focusAndRestore = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.focus();
      restoreSelection();
    }
  }, [restoreSelection]);

  const execCommand = useCallback(
    (command, value = null) => {
      saveSelection();
      focusAndRestore();
      document.execCommand(command, false, value);
      updateActiveStates();
      setTimeout(() => {
        focusAndRestore();
        // Also trigger onChange after command (optional, but helpful)
        if (onChange && editorRef.current) {
          onChange(editorRef.current.innerHTML);
        }
      }, 10);
    },
    [saveSelection, focusAndRestore, onChange]
  );

  const updateActiveStates = useCallback(() => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyRight: document.queryCommandState('justifyRight'),
      insertUnorderedList: document.queryCommandState('insertUnorderedList'),
      insertOrderedList: document.queryCommandState('insertOrderedList'),
    });
  }, []);

  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0 && editorRef.current?.contains(selection.anchorNode)) {
        updateActiveStates();
      }
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [updateActiveStates]);

  const insertLink = () => {
    const url = window.prompt('Enter URL:', 'https://');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const removeLink = () => {
    execCommand('unlink');
  };

  const formatBlock = (tag) => {
    execCommand('formatBlock', tag);
  };

  const handleFontSize = (size) => {
    execCommand('fontSize', size);
  };

  const handleFontFamily = (font) => {
    execCommand('fontName', font);
  };

  const handleColor = (color) => {
    execCommand('foreColor', color);
  };

  return (
    <EditorContainer>
      <ToolbarWrapper>
        <ToolbarGroup>
          <IconButton onClick={() => execCommand('bold')} $active={activeFormats.bold} title="Bold (Ctrl+B)">
            <Icons.Bold />
          </IconButton>
          <IconButton onClick={() => execCommand('italic')} $active={activeFormats.italic} title="Italic (Ctrl+I)">
            <Icons.Italic />
          </IconButton>
          <IconButton onClick={() => execCommand('underline')} $active={activeFormats.underline} title="Underline (Ctrl+U)">
            <Icons.Underline />
          </IconButton>
        </ToolbarGroup>

        <ToolbarGroup>
          <IconButton onClick={() => execCommand('justifyLeft')} $active={activeFormats.justifyLeft} title="Align Left">
            <Icons.AlignLeft />
          </IconButton>
          <IconButton onClick={() => execCommand('justifyCenter')} $active={activeFormats.justifyCenter} title="Align Center">
            <Icons.AlignCenter />
          </IconButton>
          <IconButton onClick={() => execCommand('justifyRight')} $active={activeFormats.justifyRight} title="Align Right">
            <Icons.AlignRight />
          </IconButton>
        </ToolbarGroup>

        <ToolbarGroup>
          <IconButton onClick={() => execCommand('insertUnorderedList')} $active={activeFormats.insertUnorderedList} title="Bullet List">
            <Icons.ListBullet />
          </IconButton>
          <IconButton onClick={() => execCommand('insertOrderedList')} $active={activeFormats.insertOrderedList} title="Numbered List">
            <Icons.ListNumber />
          </IconButton>
          <IconButton onClick={() => execCommand('formatBlock', 'blockquote')} title="Quote">
            <Icons.Quote />
          </IconButton>
        </ToolbarGroup>

        <ToolbarGroup>
          <StyledSelect onChange={(e) => formatBlock(e.target.value)} defaultValue="p">
            <option value="p">Normal</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="h4">Heading 4</option>
          </StyledSelect>

          <StyledSelect onChange={(e) => handleFontFamily(e.target.value)}>
            <option value="Inter">Inter</option>
            <option value="Arial">Arial</option>
            <option value="Poppins">Poppins</option>
            <option value="Georgia">Georgia</option>
            <option value="Courier New">Courier New</option>
          </StyledSelect>

          <StyledSelect onChange={(e) => handleFontSize(e.target.value)}>
            <option value="1">Small</option>
            <option value="3" selected>Normal</option>
            <option value="5">Large</option>
            <option value="7">Huge</option>
          </StyledSelect>

          <ColorPicker type="color" onChange={(e) => handleColor(e.target.value)} title="Text color" />
        </ToolbarGroup>

        <ToolbarGroup>
          <IconButton onClick={insertLink} title="Insert Link">
            <Icons.Link />
          </IconButton>
          <IconButton onClick={removeLink} title="Remove Link">
            <Icons.Unlink />
          </IconButton>
          <IconButton onClick={() => execCommand('undo')} title="Undo">
            <Icons.Undo />
          </IconButton>
          <IconButton onClick={() => execCommand('redo')} title="Redo">
            <Icons.Redo />
          </IconButton>
          <IconButton onClick={() => execCommand('removeFormat')} title="Clear formatting">
            <Icons.Clear />
          </IconButton>
        </ToolbarGroup>
      </ToolbarWrapper>

      <EditorArea
        ref={setRefs}
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={handleInput}
        onMouseUp={updateActiveStates}
        onKeyUp={updateActiveStates}
      />
    </EditorContainer>
  );
});

ProEditor.displayName = 'ProEditor';

export default ProEditor;